import React from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProfileIcon } from '../../utils/profileIcons';
import type { ProfileIconKey } from '../../utils/profileIcons';
import { useTerminology } from '../../hooks/useTerminology';

/**
 * The profile's card system.
 *
 * Radius, border, surface, padding, header structure, icon treatment and
 * dark-theme behaviour are decided ONCE here, so no card on the page is styled
 * individually and none can drift. Every colour is a token, so light and dark
 * are the same component.
 */
export function ProfileCard({
  children,
  className = '',
  interactive = false
}: {
  children: React.ReactNode;
  className?: string;
  interactive?: boolean;
}) {
  return <section className={`flex min-w-0 flex-col rounded-2xl border border-line bg-surface ${interactive ? 'transition-colors duration-150 ease-out hover:border-line-strong' : ''} ${className}`} data-id="element-4137" data-mp="cok44yd3eic5">
      {children}
    </section>;
}

/** The tinted square an icon sits in. One size, one radius, one treatment. */
export function ProfileCardIcon({
  name,
  tone = 'brand'
}: {
  name: ProfileIconKey;
  tone?: 'brand' | 'info' | 'warn' | 'ok' | 'neutral';
}) {
  const TONES = {
    brand: 'bg-brand-50 text-brand-800',
    info: 'bg-info-50 text-info-700',
    warn: 'bg-warn-50 text-warn-700',
    ok: 'bg-ok-50 text-ok-700',
    neutral: 'bg-canvas text-ink-muted'
  } as const;
  return <span aria-hidden className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONES[tone]}`} data-id="element-4138" data-mp="359nbycfedi76">
      <ProfileIcon name={name} className="h-[18px] w-[18px]" data-id="element-4139" data-mp="318tlpcehfrb4" />
    </span>;
}
export function ProfileCardHeader({
  icon,
  title,
  description,
  tone,
  action
}: {
  icon?: ProfileIconKey;
  title: string;
  description?: string;
  tone?: 'brand' | 'info' | 'warn' | 'ok' | 'neutral';
  action?: React.ReactNode;
}) {
  const {
    phrase
  } = useTerminology();
  return <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 pt-5" data-id="element-4140" data-mp="1dm7ebqn5ffdk">
      <div className="flex min-w-0 items-center gap-3" data-id="element-4141" data-mp="1icfg1m6l21qb">
        {icon && <ProfileCardIcon name={icon} tone={tone} data-id="element-4142" data-mp="1idyztmrg6uwu" />}
        <div className="min-w-0" data-id="element-4143" data-mp="14m27pips4l0g">
          <h2 className="text-h4 font-semibold text-ink" data-id="element-4144" data-mp="14mu611argwuf">{phrase(title)}</h2>
          {description && <p className="mt-0.5 max-w-[52ch] text-body-sm text-ink-muted" data-id="element-4145" data-mp="19kluflc760tv">{phrase(description)}</p>}
        </div>
      </div>
      {action && <div className="shrink-0" data-id="element-4146" data-mp="21c1sqcn49bzx">{action}</div>}
    </header>;
}

/**
 * A row that leads somewhere. Rendered as a link or a button — never a div with
 * a click handler — so it is reachable by keyboard and announced correctly.
 */
export function ProfileActionRow({
  icon,
  tone = 'neutral',
  label,
  description,
  trailing,
  to,
  onClick,
  danger = false
}: {
  icon: ProfileIconKey;
  tone?: 'brand' | 'info' | 'warn' | 'ok' | 'neutral';
  label: string;
  description?: string;
  trailing?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  danger?: boolean;
}) {
  const {
    phrase
  } = useTerminology();
  const body = <>
      <ProfileCardIcon name={icon} tone={danger ? 'warn' : tone} data-id="element-4147" data-mp="4cknkip2h46u" />
      <span className="min-w-0 flex-1 text-left" data-id="element-4148" data-mp="id3u1ldvw26w">
        <span className={`block truncate text-body font-medium ${danger ? 'text-danger-700' : 'text-ink'}`} data-id="element-4149" data-mp="iare1dros1wh">
          {phrase(label)}
        </span>
        {description && <span className="mt-0.5 block text-body-sm text-ink-muted" data-id="element-4150" data-mp="3lrhqobhrxd75">{phrase(description)}</span>}
      </span>
      {trailing}
      <ChevronRightIcon aria-hidden className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-4151" data-mp="3vj40c3egr47s" />
    </>;
  const classes = `flex min-h-touch w-full items-center gap-3 px-5 py-3.5 text-left transition-colors duration-150 ease-out ${danger ? 'hover:bg-danger-50' : 'hover:bg-canvas'}`;
  if (to) {
    return <Link to={to} className={classes} data-id="element-4152" data-mp="2y4jdpnsv4ndz">
        {body}
      </Link>;
  }
  return <button type="button" onClick={onClick} className={classes} data-id="element-4153" data-mp="3bqyvth75cdw7">
      {body}
    </button>;
}

/** A labelled value with an edit affordance and an honest empty state. */
export function ProfileField({
  icon,
  label,
  value,
  emptyLabel,
  onEdit
}: {
  icon: ProfileIconKey;
  label: string;
  value: string | null;
  emptyLabel: string;
  onEdit?: () => void;
}) {
  const {
    phrase
  } = useTerminology();
  return <li className="flex items-start gap-3 px-5 py-3.5" data-id="element-4154" data-mp="3uhzppqtetj8w">
      <ProfileCardIcon name={icon} tone="neutral" data-id="element-4155" data-mp="3ujklfjrgn88c" />
      <div className="min-w-0 flex-1" data-id="element-4156" data-mp="39vqz4vrl8lp">
        <p className="text-body font-medium text-ink" data-id="element-4157" data-mp="3czzgeg9iqi6">{phrase(label)}</p>
        {value ? <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-4158" data-mp="87x65eq2knrw">{value}</p> : <button type="button" onClick={onEdit} className="mt-0.5 text-body-sm font-medium text-brand-700 hover:underline" data-id="element-4159" data-mp="d8kz45xuzeor">
            {phrase(emptyLabel)} →
          </button>}
      </div>
      {onEdit && value && <button type="button" onClick={onEdit} className="flex min-h-touch min-w-touch items-center justify-center rounded-lg text-ink-subtle transition-colors duration-150 ease-out hover:text-ink" aria-label={`Edit ${phrase(label)}`} data-id="element-4160" data-mp="w6d8f8deku2m">
          <ProfileIcon name="preferences" className="h-4 w-4" data-id="element-4161" data-mp="ieq1melu435l" />
        </button>}
    </li>;
}

/** Skeleton shaped like the real card, so loading does not reflow the page. */
export function ProfileCardSkeleton({
  rows = 3
}: {
  rows?: number;
}) {
  return <ProfileCard className="p-5" data-id="element-4162" data-mp="1b0rmqvm0vfay">
      <div className="cz-skeleton h-5 w-40 rounded bg-canvas" data-id="element-4163" data-mp="162q3oaoljfzh" />
      <div className="mt-4 space-y-3" data-id="element-4164" data-mp="165c65vwmr5au">
        {Array.from({
        length: rows
      }).map((_, index) => <div key={index} className="flex items-center gap-3" data-id="element-4165" data-mp="116soi7p2kfoa" data-mpi={index}>
            <div className="cz-skeleton h-9 w-9 rounded-lg bg-canvas" data-id="element-4166" data-mp="3qvbck0y116bc" />
            <div className="flex-1 space-y-1.5" data-id="element-4167" data-mp="3ludurpgxiead">
              <div className="cz-skeleton h-3.5 w-1/3 rounded bg-canvas" data-id="element-4168" data-mp="3lwqadvz7sck9" />
              <div className="cz-skeleton h-3 w-2/3 rounded bg-canvas" data-id="element-4169" data-mp="3ncc8di5hnu03" />
            </div>
          </div>)}
      </div>
    </ProfileCard>;
}